import React from "react";
import { ChevronDown, Lightbulb } from "lucide-react";

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const buildPath = (data, key, maxValue) => {
  const width = 720;
  const height = 260;
  const xStep = width / Math.max(data.length - 1, 1);

  return data
    .map((item, index) => {
      const x = index * xStep;
      const y = height - ((Number(item[key]) || 0) / maxValue) * height;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
};

const CashFlow = ({ data = [] }) => {
  const incoming = data.reduce((sum, item) => sum + (Number(item.incoming) || 0), 0);
  const outgoing = data.reduce((sum, item) => sum + (Number(item.outgoing) || 0), 0);
  const net = incoming - outgoing;
  const maxValue = Math.max(...data.flatMap((item) => [Number(item.incoming) || 0, Number(item.outgoing) || 0]), 1);
  const gridLines = [0, 1, 2, 3, 4].map((line) => 260 - line * 65);

  return (
    <section className="zarp-chart-card">
      <div className="zarp-section-header">
        <div>
          <h2>Cash Flow Overview</h2>
          <div className="zarp-legend">
            <span><i className="incoming" /> Incoming</span>
            <span><i className="outgoing" /> Outgoing</span>
          </div>
        </div>
        <div className="zarp-chart-actions">
          <button>This Month <ChevronDown size={15} /></button>
        </div>
      </div>

      <div className="zarp-chart-layout">
        <div className="zarp-chart-canvas">
          <svg className="zarp-svg-chart" viewBox="-42 -18 790 320" role="img" aria-label="Incoming and outgoing cash flow chart">
            {gridLines.map((y) => (
              <g key={y}>
                <line x1="0" x2="720" y1={y} y2={y} className="zarp-grid-line" />
                <text x="-40" y={y + 4} className="zarp-axis-text">
                  ₹ {Math.round(((260 - y) / 260) * maxValue / 1000)}K
                </text>
              </g>
            ))}

            <path d={buildPath(data, "incoming", maxValue)} className="zarp-line incoming" />
            <path d={buildPath(data, "outgoing", maxValue)} className="zarp-line outgoing" />

            {data.map((item, index) => {
              const x = index * (720 / Math.max(data.length - 1, 1));
              return (
                <text key={item.date} x={x} y="294" className="zarp-axis-text zarp-x-label" textAnchor={index === 0 ? "start" : index === data.length - 1 ? "end" : "middle"}>
                  {item.date}
                </text>
              );
            })}
          </svg>
        </div>

        <aside className="zarp-cash-summary">
          <div>
            <span>Incoming</span>
            <strong className="incoming">{money(incoming)}</strong>
          </div>
          <div>
            <span>Outgoing</span>
            <strong className="outgoing">{money(outgoing)}</strong>
          </div>
          <div>
            <span>Cash as on May 31</span>
            <strong>{money(net)}</strong>
          </div>
        </aside>
      </div>

      <div className="zarp-chart-note">
        <Lightbulb size={16} />
        Cash flow looks good. Keep tracking overdue collections closely.
      </div>
    </section>
  );
};

export default CashFlow;
